import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";

export default function Login() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, loginGoogle } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      nav(next);
    } catch (e) {
      setErr(e.response?.data?.detail || "Erro ao autenticar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 pb-24 min-h-screen organic-mesh" data-testid="login-page">
      <div className="max-w-md mx-auto px-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-3xl p-8 md:p-10 shadow-glass border border-black/5"
        >
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <img src="/assets/biofarmax-logo.png" alt="Biofarmax" className="w-16 h-16 object-contain mx-auto" />
            </Link>
            <h1 className="h-display text-4xl mb-2">
              {mode === "login" ? "Bem-vindo de volta" : "Crie sua conta"}
            </h1>
            <p className="text-sm text-ink-secondary">
              {mode === "login"
                ? "Entre para acompanhar seus pedidos e receitas."
                : "Cadastre-se e tenha sua manipulação personalizada."}
            </p>
          </div>

          <button
            onClick={loginGoogle}
            className="w-full flex items-center justify-center gap-3 border border-black/10 rounded-full py-3 mb-4 hover:bg-bg-secondary transition font-tech text-sm"
            data-testid="google-login-btn"
          >
            <svg width="20" height="20" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 3l5.7-5.7C33.7 6.1 29.1 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 12 24 12c3 0 5.7 1.1 7.8 3l5.7-5.7C33.7 6.1 29.1 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5 0 9.6-1.9 13-5l-6-5.2c-1.9 1.3-4.3 2.1-7 2.1-5.2 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6 5.2C40.8 35.5 44 30.2 44 24c0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            Continuar com Google
          </button>

          <div className="relative my-6 text-center">
            <span className="bg-white px-3 text-xs uppercase tracking-widest text-ink-secondary font-tech relative z-10">
              ou
            </span>
            <div className="absolute left-0 right-0 top-1/2 h-px bg-black/10 -z-0" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-xs font-tech uppercase tracking-widest text-ink-secondary">Nome</label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full mt-2 px-4 py-3 rounded-xl border border-black/10 focus:border-brand-primary focus:outline-none"
                  data-testid="register-name-input"
                />
              </div>
            )}
            <div>
              <label className="text-xs font-tech uppercase tracking-widest text-ink-secondary">E-mail</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-2 px-4 py-3 rounded-xl border border-black/10 focus:border-brand-primary focus:outline-none"
                data-testid="auth-email-input"
              />
            </div>
            <div>
              <label className="text-xs font-tech uppercase tracking-widest text-ink-secondary">Senha</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-2 px-4 py-3 rounded-xl border border-black/10 focus:border-brand-primary focus:outline-none"
                data-testid="auth-password-input"
              />
            </div>
            {err && <div className="text-sm text-status-error" data-testid="auth-error">{err}</div>}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-60"
              data-testid="auth-submit-btn"
            >
              {loading ? "Aguarde..." : mode === "login" ? "Entrar" : "Criar conta"}
            </button>
          </form>

          <div className="text-center mt-6 text-sm text-ink-secondary">
            {mode === "login" ? "Ainda não tem conta?" : "Já tem conta?"}{" "}
            <button
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
              className="text-brand-accent hover:underline font-tech"
              data-testid="toggle-auth-mode"
            >
              {mode === "login" ? "Cadastre-se" : "Entrar"}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
